import { cn, focusRing, type MenuHeading, type MenuItem, MoreIcon } from "@rhema/ui";
import type { ReactNode } from "react";
import { useIsDesktop } from "../app/media";
import { useItemMenu } from "./useItemMenu";

/**
 * Gives an item its menu: right-click on desktop, a long press on phones, the context menu key, and
 * on desktop a "…" button that shows on hover or focus. An item whose menu would be empty, such as
 * your own row in a member list, has no "…" and does not open.
 */
export function WithMenu({
  label,
  items,
  heading,
  as: Tag = "li",
  className,
  buttonClassName,
  more = true,
  children,
}: {
  /** What the item is, such as "Ruth Adeyemi" or "#general". */
  label: string;
  items: () => MenuItem[];
  /** The phone sheet's title row. Defaults to the label. */
  heading?: MenuHeading;
  as?: "li" | "div";
  className?: string;
  buttonClassName?: string;
  /** Whether desktop shows the "…" button on hover. */
  more?: boolean;
  children: ReactNode;
}) {
  const menu = useItemMenu(`Options for ${label}`, items, heading ?? { title: label });
  const desktop = useIsDesktop();
  const hasItems = more && desktop && items().length > 0;
  return (
    // A long press opens the menu, so the phone's own link preview is held back on the row.
    <Tag className={cn("group relative [-webkit-touch-callout:none]", className)} {...menu.bind}>
      {children}
      {hasItems && (
        <button
          type="button"
          aria-label={`Options for ${label}`}
          aria-haspopup="menu"
          aria-expanded={menu.open}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            menu.openFrom(e.currentTarget);
          }}
          className={cn(
            "absolute top-1/2 right-3 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-surface-header text-ink-secondary opacity-0 shadow-sm hover:text-ink",
            "group-hover:opacity-100 focus-visible:opacity-100",
            menu.open && "opacity-100",
            focusRing,
            buttonClassName,
          )}
        >
          <MoreIcon size={17} />
        </button>
      )}
      {menu.menu}
    </Tag>
  );
}

/** The "…" button for a screen's header, which opens the same menu as the item's row. */
export function MoreButton({
  label,
  items,
  heading,
  className,
}: {
  label: string;
  items: () => MenuItem[];
  heading?: MenuHeading;
  className?: string;
}) {
  const menu = useItemMenu(`Options for ${label}`, items, heading ?? { title: label });
  if (items().length === 0) return null;
  return (
    <>
      <button
        type="button"
        aria-label={`Options for ${label}`}
        aria-haspopup="menu"
        aria-expanded={menu.open}
        onClick={(e) => menu.openFrom(e.currentTarget)}
        className={cn(
          "flex h-10 w-10 flex-none cursor-pointer items-center justify-center rounded-full text-ink hover:bg-surface-header",
          focusRing,
          className,
        )}
      >
        <MoreIcon size={20} />
      </button>
      {menu.menu}
    </>
  );
}
